import { CSSProperties, ReactNode } from 'react'
import { AlertCircle, AlertTriangle, CheckCircle, Info, X } from 'lucide-react'
import { cn } from 'utils/cn'

export interface AlertProps {
    variant?: 'default' | 'accent' | 'success' | 'warning' | 'danger'
    title: string
    description?: string
    icon?: ReactNode
    actionLabel?: string
    onAction?: () => void
    onClose?: () => void
    style?: CSSProperties
}

const defaultIcons: Record<string, ReactNode> = {
    default: <Info size={16} strokeWidth={1.5} />,
    accent: <Info size={16} strokeWidth={1.5} />,
    success: <CheckCircle size={16} strokeWidth={1.5} />,
    warning: <AlertTriangle size={16} strokeWidth={1.5} />,
    danger: <AlertCircle size={16} strokeWidth={1.5} />,
}

const titleColorClasses: Record<string, string> = {
    default: 'text-foreground',
    accent: 'text-foreground',
    success: 'text-success',
    warning: 'text-warning',
    danger: 'text-danger',
}

const actionClasses: Record<string, string> = {
    default: 'bg-default text-default-foreground',
    accent: 'bg-accent text-accent-foreground',
    success: 'bg-success text-success-foreground',
    warning: 'bg-warning text-warning-foreground',
    danger: 'bg-danger text-danger-foreground',
}

export const Alert = ({ variant = 'default', title, description, icon, actionLabel, onAction, onClose, style }: AlertProps) => {
    const displayIcon = icon !== undefined ? icon : defaultIcons[variant]

    return (
        <div role="alert" className="relative flex items-start gap-2 rounded-3xl bg-surface px-3 shadow-overlay" style={style}>
            {/* Icon */}
            {displayIcon && (
                <span className={cn('mt-3 flex h-5 w-5 shrink-0 items-center', titleColorClasses[variant])}>
                    <span className="flex size-4 items-center justify-center">{displayIcon}</span>
                </span>
            )}

            {/* Content */}
            <div className="flex min-w-0 flex-1 flex-col items-start py-3 text-sm leading-[1.43]">
                <span className={cn('w-full font-medium', titleColorClasses[variant])}>{title}</span>
                {description && <span className="w-full text-foreground-muted">{description}</span>}
            </div>

            {/* Actions */}
            <div className="flex shrink-0 items-start justify-center gap-3 self-stretch py-3">
                {actionLabel && (
                    <button
                        type="button"
                        onClick={onAction}
                        className={cn(
                            'flex h-9 items-center justify-center whitespace-nowrap rounded-3xl px-4 text-sm font-medium leading-5',
                            actionClasses[variant]
                        )}
                    >
                        {actionLabel}
                    </button>
                )}

                {/* Close button */}
                {onClose && (
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        className="absolute -right-6 -top-2 flex items-center rounded-xl bg-default p-1 text-default-foreground"
                    >
                        <X size={16} strokeWidth={1.5} />
                    </button>
                )}
            </div>
        </div>
    )
}
